import React from 'react';
import { useScrollReveal } from './useScrollReveal';

const BEFORE = [
  'Scattered Pinterest boards, 15 open browser tabs, and screenshot folders',
  'Last-minute budget shocks when buying decor and supplies piecemeal',
  'Incompatible color schemes and mismatched theme aesthetics',
  'Hours spent cross-referencing prices, store availability, and shipping times',
];

const AFTER = [
  'One unified studio connecting themes, moodboards, menu, and timeline',
  'Transparent cost estimates and Rent vs. Buy savings before you spend',
  'Curated, professional palettes and 4-zone room layouts',
  'Priority-sorted shopping list ready to review and purchase in minutes',
];

export default function ProblemSection() {
  const reveal = useScrollReveal();

  return (
    <section className="py-24 sm:py-32 px-4 sm:px-6 bg-white relative overflow-hidden w-full max-w-full">
      <div className="max-w-5xl mx-auto w-full">

        {/* Section header */}
        <div className="text-center mb-16 sr-item" ref={reveal}>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase mb-3 bg-stone-100 text-stone-700 border border-stone-200">
            The Difference
          </div>
          <h2
            className="font-extrabold leading-tight text-stone-950 tracking-tight"
            style={{ fontSize: 'clamp(2rem, 4.2vw, 3rem)' }}
          >
            Party planning shouldn't feel like a{' '}
            <span className="font-serif italic font-normal text-stone-700">second job.</span>
          </h2>
          <p className="mt-3 text-base sm:text-lg text-stone-500 max-w-lg mx-auto">
            Traditional hosting is chaotic and disjointed. We replace the stress with quiet clarity.
          </p>
        </div>

        {/* Comparison Grid */}
        <div className="grid sm:grid-cols-2 gap-6 sr-item" ref={reveal}>

          {/* Before Card */}
          <div className="rounded-3xl p-7 sm:p-8 bg-stone-50 border border-stone-200/90 shadow-xs flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-6">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold bg-stone-200 text-stone-600">
                  ✕
                </div>
                <p className="font-bold text-base text-stone-800">Without CraftMyParty</p>
              </div>
              <ul className="space-y-4">
                {BEFORE.map((text, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-stone-600 leading-relaxed">
                    <span className="text-stone-400 mt-0.5 text-base font-bold shrink-0">—</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-8 pt-4 border-t border-stone-200/60 text-xs font-medium text-stone-400">
              Fragmented & stressful
            </div>
          </div>

          {/* After Card */}
          <div className="rounded-3xl p-7 sm:p-8 bg-stone-900 text-white shadow-xl flex flex-col justify-between relative overflow-hidden">
            {/* Subtle glow in background */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10">
              <div className="flex items-center gap-2.5 mb-6">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  ✓
                </div>
                <p className="font-bold text-base text-white">With CraftMyParty Studio</p>
              </div>
              <ul className="space-y-4">
                {AFTER.map((text, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-stone-300 leading-relaxed">
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" className="mt-0.5 shrink-0">
                      <circle cx="8" cy="8" r="8" fill="#10B981" opacity="0.25" />
                      <path d="M5 8l2 2 4-4" stroke="#34D399" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-8 pt-4 border-t border-stone-800 text-xs font-medium text-emerald-400 flex items-center justify-between relative z-10">
              <span>Organized, predictable & inspiring</span>
              <span className="text-[11px] bg-stone-800 px-2 py-0.5 rounded-full text-stone-400">Done in 2 mins</span>
            </div>
          </div>

        </div>

        {/* Bridge line */}
        <p className="text-center mt-12 text-sm sm:text-base font-medium text-stone-500 sr-item" ref={reveal}>
          Experience the calm of having your entire event planned before you buy a single balloon.
        </p>

      </div>
    </section>
  );
}
